// Moving files to and from the server in groups: small files travel together in one request
// (see blobbatch.js), several requests run at once, and a dropped request is tried again.
import { api, ApiError, request, xhrUpload } from "./api.js";
import { decodeBatch, encodeBatch, planBatches } from "./blobbatch.js";
import { readPack } from "./pack.js";

export const UPLOAD_CONCURRENCY = 6;
export const PACK_FETCH_CONCURRENCY = 6;
const PART_CONCURRENCY = 3;
const TRIES = 3;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** A dropped connection or a server error may pass on a second try; a refusal will not. */
const worthRetrying = (err) => err?.name !== "AbortError" && (!(err instanceof ApiError) || err.status === 0 || err.status >= 500);

export async function withRetry(fn) {
  for (let attempt = 1; ; attempt++) {
    try {
      return await fn();
    } catch (err) {
      if (attempt >= TRIES || !worthRetrying(err)) throw err;
      await sleep(1000 * 2 ** (attempt - 1));
    }
  }
}

/** Run the async functions with at most `limit` at a time. */
export async function runPool(tasks, limit) {
  let next = 0;
  const worker = async () => {
    while (next < tasks.length) await tasks[next++]();
  };
  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
}

async function uploadMultipart(fileBase, file, partSize, onProgress) {
  const { upload_id } = await withRetry(() => api.post(`${fileBase}/multipart`, {}));
  const count = Math.ceil(file.size / partSize);
  const loaded = new Array(count).fill(0);
  const parts = new Array(count);
  const report = () => onProgress(loaded.reduce((a, b) => a + b, 0));
  const jobs = Array.from({ length: count }, (_, i) => async () => {
    const chunk = file.slice(i * partSize, (i + 1) * partSize);
    parts[i] = await withRetry(() =>
      xhrUpload("PUT", `${fileBase}/multipart/${i + 1}?upload_id=${encodeURIComponent(upload_id)}`, chunk, {
        onProgress: (n) => {
          loaded[i] = n;
          report();
        },
      }),
    );
    loaded[i] = chunk.size;
    report();
  });
  await runPool(jobs, PART_CONCURRENCY);
  await withRetry(() => api.post(`${fileBase}/multipart/complete`, { upload_id, parts }));
}

/**
 * Send files into a session. items: [{ id, file }]; base: `<api>/workspaces/<id>`.
 * Files that fit in a request go together (one request carries about 16 MB, once batches are allowed, see blobbatch.js), a file over
 * `partSize` goes in parts, and up to UPLOAD_CONCURRENCY requests run at once.
 * onProgress(bytesSent, filesDone): filesDone counts the files whose upload has ended, arrived or failed.
 * Returns [{ id, error }] for the files that did not arrive.
 */
export async function uploadFiles(base, items, partSize, onProgress = () => {}) {
  const failures = [];
  const sent = [];
  const tasks = [];
  let ended = 0;
  const report = () => onProgress(sent.reduce((a, b) => a + b, 0), ended);
  const add = (members, bytes, run) => {
    const slot = sent.length;
    sent.push(0);
    tasks.push(async () => {
      try {
        await run((n) => {
          sent[slot] = Math.min(n, bytes);
          report();
        });
        sent[slot] = bytes;
      } catch (err) {
        for (const m of members) failures.push({ id: m.id, error: err });
      }
      ended += members.length;
      report();
    });
  };
  for (const item of items.filter((i) => i.file.size > partSize)) {
    add([item], item.file.size, (progress) => uploadMultipart(`${base}/files/${item.id}`, item.file, partSize, progress));
  }
  for (const group of planBatches(items.filter((i) => i.file.size <= partSize), (i) => i.file.size)) {
    const bytes = group.reduce((n, i) => n + i.file.size, 0);
    if (group.length === 1) {
      const [item] = group;
      add(group, bytes, (progress) => withRetry(() => xhrUpload("PUT", `${base}/files/${item.id}/content`, item.file, { onProgress: progress })));
    } else {
      add(group, bytes, (progress) => withRetry(() => xhrUpload("PUT", `${base}/files/batch`, encodeBatch(group.map((i) => ({ k: i.id, blob: i.file }))), { onProgress: progress })));
    }
  }
  await runPool(tasks, UPLOAD_CONCURRENCY);
  return failures;
}

/** The body of a response, with progress as it arrives. A cut connection becomes an ApiError. */
async function readBody(resp, onBytes) {
  const chunks = [];
  let got = 0;
  try {
    const reader = resp.body.getReader();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      got += value.length;
      onBytes(got);
    }
    return await new Blob(chunks).arrayBuffer();
  } catch (err) {
    throw new ApiError(0, `The connection dropped while downloading (${err.message || "network error"})`);
  }
}

/**
 * Fetch the packs of files the server has opened. files: [{ id, name, size }] with size = the
 * pack's size; base: `<api>/workspaces/<id>`. Small packs come together in one response. Each
 * pack is handed to `store(fileId, pack)` as soon as its group has arrived, so one failed group
 * does not lose the others. onBytes(totalReceived). Returns [{ id, error }] for the files not stored.
 */
export async function downloadPacks(base, files, store, onBytes = () => {}) {
  const failures = [];
  const received = [];
  const stored = new Set();
  const report = () => onBytes(received.reduce((a, b) => a + b, 0));
  const tasks = planBatches(files, (f) => f.size).map((group, slot) => async () => {
    received[slot] = 0;
    try {
      const buffer = await withRetry(() => {
        received[slot] = 0;
        // One pack: its own request, which the Worker serves straight from storage. Only a group goes through /packs.
        const asked = group.length === 1 ? request("GET", `${base}/files/${group[0].id}/pack`, { raw: true }) : request("POST", `${base}/packs`, { json: { file_ids: group.map((f) => f.id) }, raw: true });
        return asked.then((resp) =>
          readBody(resp, (n) => {
            received[slot] = n;
            report();
          }),
        );
      });
      const parts = group.length === 1 ? [{ k: group[0].id, data: buffer }] : decodeBatch(buffer);
      for (const { k, data } of parts) {
        await store(k, await readPack(data));
        stored.add(k);
      }
    } catch (err) {
      for (const f of group) if (!stored.has(f.id)) failures.push({ id: f.id, error: err });
    }
    report();
  });
  await runPool(tasks, PACK_FETCH_CONCURRENCY);
  return failures;
}
